import { filter, identity, pipe } from 'ramda'
import { gql, useMutation } from '@apollo/client'
import { useNotification } from '@btime/react-messager'

import { documentsClient } from '../apollo/api'
import { isImage } from '@btime/helpers'
import compressor from '../helpers/compress'

export const compressImage = async (file, quality = 0.2) => {
  if (isImage(file)) {
    const compressedImage = await compressor(file, quality)
    return compressedImage
  }
  return file.data || file
}

export const dataURLtoFile = (url, filename, mimeType) => {
  return fetch(url)
    .then(function (res) {
      return res.arrayBuffer()
    })
    .then(function (buf) {
      return new File([buf], filename, { type: mimeType })
    })
}

const MUTATION = gql`
  mutation UpsertDocument($input: DocumentUploadInput!) {
    insertDocument(input: $input) {
      id
      src
      type
    }
  }
`

export const useUploadRequest = (onSuccess, onError) => {
  const notify = useNotification()

  const [request, state] = useMutation(MUTATION, { client: documentsClient })

  const handleSuccess = document => {
    notify.success({ title: `Sucesso ao carregar o(s) arquivo(s)!` })
    return (onSuccess || identity)(document)
  }

  // `error` pode chegar em duas formas bem diferentes aqui: um array de
  // erros do GraphQL (`resp.errors`, tratado explicitamente em
  // `handleRequest` abaixo) OU um único Error/ApolloError quando a própria
  // Promise da mutation rejeita (o caminho padrão do Apollo para erro de
  // rede/GraphQL sem `errorPolicy: 'all'`, e o mais comum na prática). O
  // código antigo assumia sempre a forma de array (`error[0].message`), o
  // que lança `TypeError` para o segundo caso — silenciando o toast de erro
  // (a exceção interrompe esta função antes do `notify.error`) e, com isso,
  // nunca chama `onSuccess`/`onChange`: a assinatura/foto some sem qualquer
  // aviso, como se nada tivesse sido preenchido.
  const getErrorMessage = error => {
    if (Array.isArray(error)) return error[0]?.message
    return error?.message
  }

  const handleError = error => {
    let title = 'Erro ao carregar o(s) arquivo(s)!'
    if (error?.code === 1) {
      title = 'Precisamos da permissão de localização para essa operação!'
    }
    notify.error({ title, message: getErrorMessage(error) })
    onError && onError(error)
  }

  const handleRequest = async document => {
    const compressedImage = await compressImage(document)

    return request({
      variables: {
        input: {
          file: compressedImage
        }
      }
    }).then(resp => {
      if (!!resp.errors) handleError(resp?.errors)
      return resp.data?.insertDocument
    })
  }

  const run = documents =>
    Promise.all(documents.map(handleRequest))
      .then(pipe(filter(identity), handleSuccess))
      .catch(handleError)

  return [run, state]
}
